// AgentCreationWizard.tsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  clearAllCaches,
  duplicateTemplateToAgent,
} from "@/services/templateService";
import { applyAgentDefaults, getDefaultFormFields } from "@/services/agentDefaults";
import { ChevronLeft, ChevronRight, CheckCircle, AlertCircle } from "lucide-react";
import {
  generateAgentSetup,
  createFormFieldsFromQuestions,
} from "@/services/agentGenerationService";
import { checkPlanLimits } from "@/lib/planLimits";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { updateOrganization } from "@/services/organizations";
import { industryService } from "@/services/industryService";

// ────────────────────────────────────────────────────────────
// TYPES
// ────────────────────────────────────────────────────────────
interface Persona {
  id: string;
  avatar_image_url: string | null;
  avatar_name: string | null;
  internal_description: string | null;
  type: string | null;
  catch_phrase: string | null;
  client_facing_description: string | null;
  organization_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ────────────────────────────────────────────────────────────
// TEMPLATE FILTERING UTILITY
// ────────────────────────────────────────────────────────────
const filterTemplatesBySelection = (
  allTemplates: any[],
  selectedPersona: Persona | null,
  organizationIndustryId: string | null
) => {
  if (!selectedPersona) return [];

  // For "Create from scratch" - no templates needed
  if (selectedPersona.id === "custom") {
    return [];
  }

  // For "More templates" - show templates with no persona
  if (selectedPersona.id === "more_templates") {
    const noPersonaTemplates = allTemplates.filter(t => !t.persona_id);
    
    // Priority: org industry first, then industry = null
    const industryMatches = noPersonaTemplates.filter(t => 
      t.industry_id === organizationIndustryId
    );
    const otherTemplates = noPersonaTemplates.filter(t => 
      !t.industry_id
    );
    
    return [...industryMatches, ...otherTemplates];
  }

  // For specific persona - show templates with matching persona_id
  const personaTemplates = allTemplates.filter(t => t.persona_id === selectedPersona.id);
  
  // Priority: org industry first, then industry = null (fallback)
  const industryMatches = personaTemplates.filter(t => 
    t.industry_id === organizationIndustryId
  );
  const fallbackTemplates = personaTemplates.filter(t => 
    !t.industry_id
  );
  
  return [...industryMatches, ...fallbackTemplates];
};

// ────────────────────────────────────────────────────────────
// STEP-INDICATORS
// ────────────────────────────────────────────────────────────
const StepIndicators = ({
  currentStep,
  totalSteps,
  onStepClick,
}: {
  currentStep: number;
  totalSteps: number;
  onStepClick: (step: number) => void;
}) => {
  const steps = [
    { number: 1, label: "Persona" },
    { number: 2, label: "Template" },
  ];

  return (
    <div className="w-full max-w-2xl mx-auto mb-12 px-4">
      <div className="flex items-center justify-between relative">
        {/* rail */}
        <div className="absolute top-[18px] left-[25%] right-[25%] h-0.5 bg-gray-200" />
        {/* progress */}
        <div
          className="absolute top-[18px] left-[25%] h-0.5 bg-[#ecb22d] transition-all duration-300"
          style={{ width: currentStep > 1 ? "50%" : "0%" }}
        />
        {/* dots */}
        {steps.map((step) => (
          <div key={step.number} className="flex flex-col items-center">
            <button
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 relative z-10 ${
                currentStep >= step.number
                  ? "bg-[#ecb22d] text-white border-2 border-[#ecb22d]"
                  : "bg-white text-gray-400 border-2 border-gray-200"
              }`}
              onClick={() => onStepClick(step.number)}
              disabled={step.number > currentStep}
            >
              {currentStep > step.number ? (
                <CheckCircle className="h-5 w-5" fill="currentColor" />
              ) : (
                step.number
              )}
            </button>
            <span
              className={`mt-2 text-sm font-medium ${
                currentStep >= step.number ? "text-[#ecb22d]" : "text-gray-500"
              }`}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// STEP 1  – PERSONA SELECTION
// ────────────────────────────────────────────────────────────
const PersonaSelectionStep = ({
  onNext,
  selectedPersona,
  setSelectedPersona,
}: {
  onNext: (persona?: Persona) => void;
  selectedPersona: Persona | null;
  setSelectedPersona: (persona: Persona | null) => void;
}) => {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);

  const moreTemplatesPersona: Persona = {
    id: "more_templates",
    avatar_image_url: null,
    avatar_name: "Explore more templates",
    internal_description: "Browse all available templates without persona filtering",
    type: null,
    catch_phrase: null,
    client_facing_description: "Access all templates across different personas and industries",
    organization_id: null,
    created_by: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const customPersona: Persona = {
    id: "custom",
    avatar_image_url: null,
    avatar_name: "Start from scratch",
    internal_description: "Create a fully custom agent from scratch",
    type: null,
    catch_phrase: null,
    client_facing_description:
      "A custom AI agent designed specifically for your business requirements",
    organization_id: null,
    created_by: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const getPersonaColor = (persona: Persona) => {
    if (persona.id === "custom") return "bg-gradient-to-b from-gray-500 to-gray-700";
    if (persona.id === "more_templates") return "bg-gradient-to-b from-slate-500 to-slate-700";

    const colors: Record<string, string> = {
      "Lead Generator": "from-amber-400 to-[#ecb22d]",
      "Lead Qualifier": "from-orange-400 to-orange-600",
      "Workflow Manager": "from-blue-500 to-blue-700",
      "Customer Support": "from-sky-400 to-sky-600",
      "Intake Specialist": "from-indigo-400 to-indigo-600",
      "Offboarding Specialist": "from-fuchsia-400 to-fuchsia-600",
      Copywriter: "from-emerald-400 to-emerald-600",
      "Business Development": "from-cyan-400 to-cyan-600",
      "Data Analyst": "from-amber-400 to-amber-600",
      eCommerce: "from-red-400 to-red-600",
      "Sales Manager": "from-purple-400 to-purple-600",
    };

    return `bg-gradient-to-b ${
      persona.type && colors[persona.type] ? colors[persona.type] : "from-blue-400 to-blue-600"
    }`;
  };

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === "left" ? -250 : 250, behavior: "smooth" });
  };

  const handleSelect = (persona: Persona) => {
    setSelectedPersona(persona);
    setTimeout(() => onNext(persona), 300);
  };

  // load data
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data, error } = await (supabase as any).from("personas").select("*");
        if (error) throw error;
        
        // Order: real personas + more templates + create from scratch
        setPersonas([...(data || []), moreTemplatesPersona, customPersona]);
      } catch (e: any) {
        toast({
          variant: "destructive",
          title: "Failed to load personas",
          description: e.message || "Unexpected error",
        });
        setPersonas([moreTemplatesPersona, customPersona]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // UI
  return (
    <div className="px-4 py-6">
      <div className="mb-12 text-center">
        <h2 className="text-3xl font-bold mb-3">Select a Persona</h2>
        <p className="text-gray-600 mb-4">
          Choose a persona that fits your agent's role, browse all templates, or build one from scratch.
        </p>
        <p className="text-xs italic text-gray-500 sm:hidden">
          Swipe left/right to view more options
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <div className="relative w-full pb-2">
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white shadow-sm rounded-full h-10 w-10 p-2 hidden sm:flex"
            onClick={() => scroll("left")}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <div
            ref={scrollRef}
            className="flex overflow-x-auto pt-2 snap-x snap-mandatory space-x-5 no-scrollbar px-3 sm:px-10 justify-center sm:justify-start"
          >
            {personas.map((persona) => (
              <div
                key={persona.id}
                className={`snap-center flex-shrink-0 w-[220px] sm:w-[230px] h-[320px] rounded-xl transition-all duration-300 cursor-pointer 
                ${
                  selectedPersona?.id === persona.id
                    ? "ring-4 ring-white shadow-xl scale-105 z-10"
                    : "hover:shadow-xl hover:scale-102"
                } ${getPersonaColor(persona)}`}
                onClick={() => handleSelect(persona)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleSelect(persona);
                    e.preventDefault();
                  }
                }}
              >
                {selectedPersona?.id === persona.id && (
                  <div className="absolute top-3 right-3 z-10">
                    <CheckCircle className="h-6 w-6 text-white fill-white drop-shadow-md" />
                  </div>
                )}

                <div className="flex flex-col h-full relative overflow-hidden rounded-xl">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-60 rounded-xl" />
                  {/* avatar */}
                  <div className="flex justify-center items-center pt-6 pb-3 relative z-10">
                    {persona.avatar_image_url ? (
                      <div className="w-32 h-32 rounded-full border-4 border-white/90 shadow-lg overflow-hidden bg-white/10">
                        <img
                          src={persona.avatar_image_url}
                          alt={persona.avatar_name || "Persona"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center text-white text-3xl font-bold border-4 border-white/90 shadow-lg">
                        {persona.id === "custom" 
                          ? "✨" 
                          : persona.id === "more_templates" 
                            ? "📁" 
                            : persona.avatar_name?.substring(0, 2).toUpperCase() || "P"}
                      </div>
                    )}
                  </div>

                  <div className="px-5 text-white z-10 text-center mt-2">
                    <h3 className="text-2xl font-bold drop-shadow-sm mb-1">
                      {persona.avatar_name}
                    </h3>
                    <p className="text-white/90 text-sm mb-3">{persona.type}</p>
                    {persona.catch_phrase && (
                      <p className="text-white/80 text-sm italic line-clamp-3">
                        "{persona.catch_phrase}"
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white shadow-sm rounded-full h-10 w-10 p-2 hidden sm:flex"
            onClick={() => scroll("right")}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// STEP 2 – TEMPLATE SELECTION
// ────────────────────────────────────────────────────────────
const TemplateSelectionStep = ({
  onBack,
  selectedPersona,
  selectedTemplate,
  setSelectedTemplate,
  onCreateAgent,
}: {
  onBack: () => void;
  selectedPersona: Persona | null;
  selectedTemplate: any | null;
  setSelectedTemplate: (t: any | null) => void;
  onCreateAgent: (t: any | null) => void;
}) => {
  const [allTemplates, setAllTemplates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { organization } = useAuth();
  const { toast } = useToast();

  // Filter templates using our clean utility function
  const filteredTemplates = filterTemplatesBySelection(
    allTemplates,
    selectedPersona,
    organization?.industry_id || null
  );

  useEffect(() => {
    (async () => {
      if (!organization) return;
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("agents")
          .select("*, industries (id, name, emoji)")
          .eq("is_template", true)
          .order("created_at", { ascending: false });
        if (error) throw error;

        setAllTemplates(data || []);
      } catch (e: any) {
        toast({
          variant: "destructive",
          title: "Failed to load templates",
          description: e.message,
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [organization]);

  const select = (t: any | null) => {
    setSelectedTemplate(t);
    setTimeout(() => onCreateAgent(t), 300);
  };

  const getStepTitle = () => {
    if (selectedPersona?.id === "more_templates") {
      return "Additional Templates";
    }
    if (selectedPersona?.id === "custom") {
      return "Choose a Template";
    }
    return "Templates for " + (selectedPersona?.avatar_name || selectedPersona?.type);
  };

  const getStepDescription = () => {
    if (selectedPersona?.id === "more_templates") {
      return "Additional available templates, organized by your industry first.";
    }
    if (selectedPersona?.id === "custom") {
      return "Select a template to start from, or build completely from scratch.";
    }
    return `Templates matching the ${selectedPersona?.avatar_name || selectedPersona?.type} persona and your industry.`;
  };

  return (
    <div className="px-4 py-6">
      <div className="mb-12 text-center">
        <h2 className="text-3xl font-bold mb-3">{getStepTitle()}</h2>
        <p className="text-gray-600 mb-4">{getStepDescription()}</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Create from scratch option - only for custom persona */}
          {selectedPersona?.id === "custom" && (
            <div
              className={`p-6 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                selectedTemplate === null
                  ? "border-[#ecb22d] bg-[#ecb22d]/10"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => select(null)}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xl font-bold">
                  ✨
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Create from Scratch</h3>
                  <p className="text-gray-600 text-sm">
                    Build a tailored agent – no template needed
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Template grid */}
          {filteredTemplates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((t) => (
                <div
                  key={t.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                    selectedTemplate?.id === t.id
                      ? "border-[#ecb22d] bg-[#ecb22d]/10"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => select(t)}
                >
                  <div>
                    <h3 className="text-base font-semibold mb-2">{t.title}</h3>
                    <p className="text-gray-600 text-sm line-clamp-3">
                      {t.description || "No description"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                {selectedPersona?.id === "more_templates" 
                  ? "No additional templates available without persona assignments."
                  : "No templates found for this persona and industry."}
              </p>
              {selectedPersona?.id === "custom" && (
                <p className="text-gray-500 text-sm">
                  You can still create a custom agent from scratch above.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between items-center mt-8">
        <Button variant="outline" onClick={onBack} className="px-6">
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// MAIN WIZARD
// ────────────────────────────────────────────────────────────
const AgentCreationWizard = ({ onClose }: { onClose: () => void }) => {
  const [currentStep, setStep] = useState(1);
  const [selectedPersona, setPersona] = useState<Persona | null>(null);
  const [selectedTemplate, setTemplate] = useState<any | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [planLimitError, setPlanError] = useState<string | null>(null);

  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const nextStep = (p?: Persona) => {
    const persona = p || selectedPersona;
    if (persona?.id === "custom") {
      handleCreateAgent(null, persona); // skip step 2
    } else {
      setStep(2);
    }
  };

  const handleCreateAgent = async (template: any | null, persona?: Persona | null) => {
    const currentPersona = persona || selectedPersona;
    if (!user || !organization) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in with an organization.",
      });
      return;
    }
    if (!currentPersona) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Select a persona first.",
      });
      return;
    }

    setSubmitting(true);
    setPlanError(null);

    try {
      const limitError = await checkPlanLimits(organization.id);
      if (limitError) {
        setPlanError(limitError.message);
        setSubmitting(false);
        return;
      }

      if (template) {
        // duplicate
        const { success, agentId, error } = await duplicateTemplateToAgent(
          template.id,
          user.id,
          organization.id
        );
        if (!success) throw new Error(error || "Duplication failed");
        clearAllCaches();
        setSuccess(true);
        setTimeout(() => {
          onClose();
          navigate(`/agents/${agentId}`);
        }, 3000);
      } else {
        // custom - apply defaults
        const agentWithDefaults = applyAgentDefaults(
          {
            title: `${currentPersona.avatar_name} Agent`,
            description: currentPersona.client_facing_description || null,
            persona_id: currentPersona.id === "custom" ? null : currentPersona.id,
          },
          organization.name || "Our Company",
          user.email || "",
          organization.industry_id
        );

        const { data, error } = await supabase
          .from("agents")
          .insert({
            ...agentWithDefaults,
            organization_id: organization.id,
            created_by: user.id,
          })
          .select()
          .single();

        if (error) throw error;

        // Create default form fields for the new agent
        const defaultFields = getDefaultFormFields().map(field => ({
          ...field,
          agent_id: data.id,
        }));

        const { error: fieldsError } = await supabase
          .from("form_fields")
          .insert(defaultFields);

        if (fieldsError) {
          console.error("Error creating default form fields:", fieldsError);
          // Don't fail the whole operation, just log the error
        }

        clearAllCaches();
        setSuccess(true);
        setTimeout(() => {
          onClose();
          navigate(`/agents/${data.id}`);
        }, 3000);
      }

      // bump active_agents_count
      try {
        const { data: org, error: orgErr } = await supabase
          .from("organizations")
          .select("active_agents_count")
          .eq("id", organization.id)
          .single();
        if (orgErr) throw orgErr;
        await updateOrganization(organization.id, {
          active_agents_count: (org?.active_agents_count || 0) + 1,
        });
      } catch (e) {
        console.error("increment count failed", e);
      }
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Error creating agent",
        description: e.message || "Unexpected error",
      });
      setSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-8">
        {/* animated success */}
        {/* … kept fancy success animation here … */}
        <h2 className="text-3xl font-bold text-gray-800 mb-4 animate-fade-in">
          🔧 Building your agent...
        </h2>
        <p className="text-gray-600 text-lg animate-fade-in">
          Your AI agent will be ready shortly
        </p>
      </div>
    );
  }

  return (
    <div className="py-6">
      <h1 className="text-xl text-center text-gray-600 mb-4">Create New Agent</h1>

      <StepIndicators currentStep={currentStep} totalSteps={2} onStepClick={(s) => s < currentStep && setStep(s)} />

      {planLimitError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Plan Limit Reached</AlertTitle>
          <AlertDescription>
            {planLimitError}
            <div className="mt-4">
              <Button onClick={() => navigate("/pricing")} variant="secondary">
                Upgrade Plan
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {currentStep === 1 && (
        <PersonaSelectionStep
          onNext={nextStep}
          selectedPersona={selectedPersona}
          setSelectedPersona={setPersona}
        />
      )}

      {currentStep === 2 && (
        <TemplateSelectionStep
          onBack={() => setStep(1)}
          selectedPersona={selectedPersona}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setTemplate}
          onCreateAgent={handleCreateAgent}
        />
      )}
    </div>
  );
};

export default AgentCreationWizard;